import React, { useContext } from "react";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { useNavigate } from "react-router-dom";

import {
  CalendarRange,
  CalendarCheck2,
  SlidersHorizontal,
  ShieldCheck,
  PlugZap,
  ReceiptIndianRupee,
  BookOpenCheck,
  BookText,
  RotateCcw,
  Upload,
  ArrowRight,
} from "lucide-react";
import AdminWrapper from "../components/AdminWrapper";

const AdminHome = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();

  // ✅ ONLY titles from image, new aesthetic design
  const menus = [
    
    {
      title: "Customer Financial Year Setup",
      desc: "Configure customer-wise year mapping",
      icon: CalendarCheck2,
      path: "/admin/CustomerFinancialYearSetup",
    },
    
    {
      title: "Rights Management",
      desc: "Roles, permissions and access rules",
      icon: ShieldCheck,
      path: "/admin/ModulesAndRightsManagement",
    },
    {
      title: "Tally Registration",
      desc: "Register and connect tally settings",
      icon: PlugZap,
      path: "/admin/BranchRegistration",
    },
    {
      title: "Tax Details",
      desc: "Tax configuration and parameters",
      icon: ReceiptIndianRupee,
      path: "/admin/CurrentTax",
    },
    {
      title: "Tax Master",
      desc: "Create and manage tax masters",
      icon: BookOpenCheck,
      path: "/admin/TaxMaster",
    },
    {
      title: "Tax Master Details",
      desc: "Maintain tax slabs / mappings",
      icon: BookText,
      path: "/admin/TaxMasterPercent",
    },
    {
      title: "Tally Reset Option",
      desc: "Reset tally sync / configuration",
      icon: RotateCcw,
      path: "/admin/AdminTallyReset",
    },
    {
      title: "Export Data",
      desc: "Export reports and master data",
      icon: Upload,
      path: "/admin/AdminExportToExcel",
    },
  ];

  const accent = `bg-${baseColor}-${baseShade}`;
  const accentSoft = isDark ? "bg-white/10" : "bg-black/5";
  const cardBase =
    "group relative w-full text-left rounded-2xl border p-5 overflow-hidden " +
    "transition-all duration-200 hover:-translate-y-1 hover:shadow-xl";

  return (
    <AdminWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADMIN HOME</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border mx-6 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black border-gray-200"
          }`}
        >
          {/* Top section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Welcome to Admin Panel
              </h1>
              <p className={`${isDark ? "text-white/60" : "text-gray-600"} mt-1`}>
                Choose a module to manage system configuration.
              </p>
            </div>

            {/* <button
              onClick={() => navigate("/adminMaster/home")}
              className={`px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${accent} hover:bg-${baseColor}-700 text-white`}
            >
              Go to Home
            </button> */}
          </div>

          {/* ✅ New aesthetic menu grid */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {menus.map((m, idx) => {
              const Icon = m.icon;
              return (
                <button
                  key={idx}
                  onClick={() => navigate(m.path)}
                  className={`${cardBase} ${
                    isDark
                      ? "border-white/10 bg-white/5 hover:bg-white/10"
                      : "border-gray-200 bg-gray-50 hover:bg-white"
                  }`}
                >
                  {/* decorative gradient blob */}
                  <div
                    className={`absolute -top-16 -right-16 h-40 w-40 rounded-full blur-2xl opacity-40 ${accent}`}
                  />
                  <div
                    className={`absolute -bottom-16 -left-16 h-40 w-40 rounded-full blur-2xl opacity-25 ${accent}`}
                  />

                  <div className="relative flex items-start gap-4">
                    {/* icon badge */}
                    <div
                      className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${accent}`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className={`font-semibold text-lg ${isDark ? "text-white" : "text-gray-900"}`}>
                          {m.title}
                        </h2>
                        <ArrowRight
                          className={`w-5 h-5 transition-all duration-200 group-hover:translate-x-1 ${
                            isDark ? "text-white/60" : "text-gray-500"
                          }`}
                        />
                      </div>

                      <p className={`text-sm mt-1 ${isDark ? "text-white/60" : "text-gray-600"}`}>
                        {m.desc}
                      </p>

                      <div className="mt-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs border ${accentSoft} ${
                            isDark ? "border-white/10 text-white/70" : "border-gray-200 text-gray-700"
                          }`}
                        >
                          Open module
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </AdminWrapper>
  );
};

export default AdminHome;
